export const SECTION_IDS = Object.freeze(['hero', 'services', 'benefits', 'providers']);
export const SERVICE_GRID_SPANS = Object.freeze([2, 3, 4, 6, 8, 12]);

const LAYOUT_GROUPS = Object.freeze({
  none: Object.freeze([]),
  horizontal: Object.freeze(['offset_x']),
  section: Object.freeze(['min_height', 'padding_top', 'padding_bottom']),
  title: Object.freeze(['offset_x', 'offset_y', 'width_percent', 'max_width', 'align']),
  verticalWidth: Object.freeze(['offset_y', 'width_percent', 'max_width', 'align']),
  action: Object.freeze(['offset_x', 'offset_y', 'width_percent', 'max_width', 'align']),
  image: Object.freeze(['offset_x', 'offset_y', 'image_frame_width', 'image_frame_height']),
  serviceCard: Object.freeze(['grid_span', 'grid_start', 'min_height']),
  benefit: Object.freeze(['width_percent', 'min_height']),
});

export const NODE_LAYOUT_PROPERTY_MATRIX = Object.freeze({
  navigation: LAYOUT_GROUPS.none,
  'navigation.brand': LAYOUT_GROUPS.horizontal,
  'navigation.login': LAYOUT_GROUPS.horizontal,
  'navigation.register': LAYOUT_GROUPS.horizontal,
  hero: LAYOUT_GROUPS.section,
  'hero.title': LAYOUT_GROUPS.title,
  'hero.subtitle': LAYOUT_GROUPS.title,
  'hero.search': LAYOUT_GROUPS.verticalWidth,
  'hero.primary_cta': LAYOUT_GROUPS.action,
  'hero.image': LAYOUT_GROUPS.image,
  services: LAYOUT_GROUPS.section,
  benefits: LAYOUT_GROUPS.section,
  providers: LAYOUT_GROUPS.section,
  'providers.title': LAYOUT_GROUPS.title,
  'providers.description': LAYOUT_GROUPS.title,
  'providers.actions': LAYOUT_GROUPS.action,
  'providers.image': LAYOUT_GROUPS.image,
  footer: LAYOUT_GROUPS.none,
});

export const layoutKeysForNode = (id) => {
  if (NODE_LAYOUT_PROPERTY_MATRIX[id]) return NODE_LAYOUT_PROPERTY_MATRIX[id];
  if (id?.startsWith('service:')) return LAYOUT_GROUPS.serviceCard;
  if (id?.startsWith('benefit:')) return LAYOUT_GROUPS.benefit;
  return LAYOUT_GROUPS.none;
};

export const supportsLayoutProperty = (id, property) => layoutKeysForNode(id).includes(property);

export const filterLayoutOverrideForNode = (id, override = {}) => Object.fromEntries(
  Object.entries(override).filter(([key]) => supportsLayoutProperty(id, key))
);

const CAPABILITY_MAP = Object.freeze({
  navigation: ['select'],
  'navigation.brand': ['move-x'],
  'navigation.login': ['move-x'],
  'navigation.register': ['move-x'],
  hero: ['reorder', 'resize-y'],
  'hero.title': ['move-x', 'move-y', 'resize-x'],
  'hero.subtitle': ['move-x', 'move-y', 'resize-x'],
  'hero.search': ['move-y', 'resize-x'],
  'hero.primary_cta': ['move-x', 'move-y', 'resize-x'],
  'hero.image': ['move-x', 'move-y', 'resize-frame'],
  services: ['reorder', 'resize-y'],
  benefits: ['reorder', 'resize-y'],
  providers: ['reorder', 'resize-y'],
  'providers.title': ['move-x', 'move-y', 'resize-x'],
  'providers.description': ['move-x', 'move-y', 'resize-x'],
  'providers.actions': ['move-x', 'move-y', 'resize-x'],
  'providers.image': ['move-x', 'move-y', 'resize-frame'],
  footer: ['select'],
});

export const capabilitiesForNode = (id) => {
  if (CAPABILITY_MAP[id]) return CAPABILITY_MAP[id];
  if (id?.startsWith('service:')) return ['reorder', 'resize-both', 'copy-style'];
  if (id?.startsWith('benefit:')) return ['reorder', 'resize-both', 'copy-style'];
  if (id?.startsWith('provider-action:')) return ['reorder', 'resize-x', 'copy-style'];
  return ['select'];
};

export const serializeCapabilities = (id) => capabilitiesForNode(id).join(',');
export const canMove = (capabilities, axis) => capabilities.includes(`move-${axis}`);
export const canResizeX = (capabilities) => capabilities.includes('resize-x') || capabilities.includes('resize-both') || capabilities.includes('resize-frame');
export const canResizeY = (capabilities) => capabilities.includes('resize-y') || capabilities.includes('resize-both') || capabilities.includes('resize-frame');
export const isFlowReorder = (capabilities) => capabilities.includes('reorder') && !canMove(capabilities, 'x') && !canMove(capabilities, 'y');

export const compatibleStyleGroup = (id) => {
  if (id?.startsWith('service:')) return 'service-card';
  if (id?.startsWith('benefit:')) return 'benefit';
  if (id?.includes('cta') || id?.startsWith('provider-action:') || id?.startsWith('navigation.')) return 'button';
  return null;
};

export const nearestServiceGridSpan = (value) => SERVICE_GRID_SPANS.reduce(
  (nearest, span) => Math.abs(span - value) < Math.abs(nearest - value) ? span : nearest,
  SERVICE_GRID_SPANS[0],
);

export const serviceGridPercent = (span) => ({
  2: 16.67,
  3: 25,
  4: 33.33,
  6: 50,
  8: 66.66,
  12: 100,
})[span];

export const SAFE_FORMAT_KEYS = Object.freeze([
  'width_percent', 'min_height', 'max_width', 'align',
]);
